/*
  xyz-lazyvideo.js — defer below-fold autoplay videos until they approach the viewport.

  WHY: the homepage shipped ~19MB of video (97.5% of page weight) because 7 `autoplay`
  videos, all 1.9-3.4 screens below the fold, begin buffering immediately. `autoplay`
  overrides preload="metadata", and `loading="lazy"` is inert on <video> (img/iframe only).
  Measured effect: an identical product fetch took 3801ms while they streamed vs 305ms
  with them stopped (12.5x) — which is what made the Quick-Add popup feel slow.

  HOW: the markup ships a poster <img> plus the real <video> inside a <template>.
  Browsers do NOT load resources inside a <template>, so nothing is fetched until we
  inject it. This mirrors the theme's own PDP pattern (.product-media__video-template
  in theme.js, which does insertAdjacentHTML from a template on demand).

  The container reserves its own box (aspect-ratio on .video-grid-item, padding-bottom on
  the ai-video-block wrapper), so hydration causes no layout shift.
*/
(function () {
  var SEL = '[data-xyz-lv]';

  function hydrate(el) {
    if (el.hasAttribute('data-xyz-lv-done')) return;
    el.setAttribute('data-xyz-lv-done', '');

    var tpl = el.querySelector('template[data-xyz-lv-tpl]');
    if (!tpl) return;

    el.insertAdjacentHTML('beforeend', tpl.innerHTML);
    tpl.parentNode.removeChild(tpl);

    var video = el.querySelector('video');
    if (!video) return;

    /* Keep the poster until there are real frames, then drop it so it can't
       sit on top of the video or linger as a stale first frame. */
    var poster = el.querySelector('.xyz-lv-poster');
    var reveal = function () {
      el.classList.add('xyz-lv-ready');
      if (poster && poster.parentNode) poster.parentNode.removeChild(poster);
    };
    video.addEventListener('loadeddata', reveal, { once: true });
    /* Safety net: if loadeddata never fires (codec issue, blocked autoplay),
       don't strand the poster forever — it stays, which is the graceful outcome. */

    /* The template markup carries `autoplay`, but a node injected after parse
       doesn't always honour it; call play() and swallow the rejection that
       browsers throw when autoplay is disallowed. */
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  /* Start fetching a bit before it scrolls in so it's playing on arrival. */
  var MARGIN = 400;

  /* The page reflows a lot while images and sections load, and a block that is
     transiently near the viewport during that settling will trip the observer
     even though its final position is far below the fold — this is exactly how
     the ai-video block hydrated at t=17s while sitting at 1538px. So re-check
     the real geometry a frame later and only commit if it still qualifies;
     otherwise stay observed and wait for a genuine scroll. */
  function stillInRange(el) {
    var b = el.getBoundingClientRect();
    if (!b.width && !b.height) return false;
    return b.top - window.innerHeight < MARGIN && b.bottom > -MARGIN;
  }

  /* Geometry is the single source of truth. IntersectionObserver and the scroll
     listener are both just triggers that ask "should anything hydrate now?" —
     neither is trusted on its own. IO alone proved unreliable (it does not
     recompute on scroll in a hidden document), and a scroll listener alone
     misses elements already in view at load. Together they cover both. */
  function sweep() {
    var pending = document.querySelectorAll(SEL + ':not([data-xyz-lv-done])');
    Array.prototype.forEach.call(pending, function (el) {
      if (stillInRange(el)) hydrate(el);
    });
    return pending.length;
  }

  var sweepTimer = null;
  function scheduleSweep() {
    if (sweepTimer) return;
    /* setTimeout, NOT requestAnimationFrame: rAF is suspended whenever the
       document is hidden (backgrounded tab, some embedded webviews), which
       would strand every video permanently unhydrated. A timer still fires.
       The delay also lets a reflow settle before we trust the geometry. */
    sweepTimer = setTimeout(function () {
      sweepTimer = null;
      if (sweep() === 0) teardown();
    }, 50);
  }

  var listening = false;
  function teardown() {
    if (!listening) return;
    listening = false;
    window.removeEventListener('scroll', scheduleSweep);
    window.removeEventListener('resize', scheduleSweep);
  }

  function boot() {
    if (!document.querySelector(SEL)) return;

    if (!listening) {
      listening = true;
      window.addEventListener('scroll', scheduleSweep, { passive: true });
      window.addEventListener('resize', scheduleSweep, { passive: true });
      /* While a document is hidden, browsers suspend both IntersectionObserver
         recomputes and scroll events — so a page opened in a background tab can
         reach the foreground with everything still unhydrated and no pending
         trigger. Sweep when it becomes visible, and on bfcache restore. */
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) scheduleSweep();
      });
      window.addEventListener('pageshow', scheduleSweep);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          var any = entries.some(function (e) {
            return e.isIntersecting;
          });
          if (any) scheduleSweep();
        },
        { rootMargin: MARGIN + 'px 0px' }
      );
      Array.prototype.forEach.call(document.querySelectorAll(SEL), function (el) {
        io.observe(el);
      });
    }

    scheduleSweep();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a full page load. */
  document.addEventListener('shopify:section:load', boot);
})();
